import { Component, OnInit, AfterViewInit } from '@angular/core';
import { Location } from '@angular/common';
import { UntypedFormBuilder, UntypedFormGroup, FormArray, UntypedFormControl, Validators, AbstractControl } from '@angular/forms';
import { firstValueFrom, Subscription, Subject, fromEvent, auditTime, takeUntil } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { PropertyService } from '../../property';
import { AlertService, ConstantService, DataCacheService } from '../../../_services';
import { ApiResponse } from '../../../_models';
import { Feature } from '@app/_components/property/feature';
import { FeatureService } from '@app/_components/property/feature';
import { RateBucketService } from '@app/_components/rate/rate-bucket';
import { DevelopmentService } from '@app/_components/development';
import { ReportService } from '@app/_components/report';
import { RUDetailedLocationService } from '@app/_components/ru-detailed-location';
import { WifiQrComponent } from '@app/_components/wifi-qr/wifi-qr.component';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { MessageService, ConfirmationService, MenuItem } from 'primeng/api';
import { VrboService } from '@app/_services/vrbo.service';


@Component({
    selector: 'app-property',
    templateUrl: './property.component.html',
    styleUrls: ['./property.component.css'],
    providers: [MessageService, ConfirmationService],
    standalone: false
})
export class PropertyComponent implements OnInit, AfterViewInit {
	wifiSecurities: { name: string; value: string; }[];
	filteredNames: any;
	locationDetailObj: any;
	locations: any[];
	sitePriorities: any;
	isRateAndYMTabSelected: boolean = false;
	/** Selected tab index for the Rates & Yield section (MatTabGroup). */
	ratesYieldSelectedIndex: number = 0;
	propertyObj: any;
	dynamicPropDesc: any;
	shortTermRentalObj: any;
	locationIds: any[];
	dynamicHeadlineChannelSpecialOffer: any;

	// left-side jump menu (PrimeNG p-menubar)
	leftMenuItems: MenuItem[] = [];
	// Scroll-spy: currently active section id (used to highlight the left nav)
	activeSectionId: string | null = null;
	private destroy$ = new Subject<void>();
	private sectionOrder: string[] = [];
	private sectionTops: Record<string, number> = {};
	private isProgrammaticScrollUntil = 0;
	private scrollSpyOffsetPx = 120;


	// Left-nav search (PrimeNG AutoComplete)
	navSearchCtrl = new UntypedFormControl(null);
	navSearchSuggestions: { label: string; id: string }[] = [];
	private navSearchOptions: { label: string; id: string }[] = [];

	private readonly sectionHeaderOffsetPx = 88; // adjust if you have a sticky header


	constructor(
		private router: Router,
		private activatedRoute: ActivatedRoute,
		private propertyService: PropertyService,
		private rateBucketService: RateBucketService,
		private reportService: ReportService,
		private dataCacheService: DataCacheService,
		private confirmationService: ConfirmationService,
		private messageService: MessageService,
		private featureService: FeatureService,
		private alertService: AlertService,
		private location: Location,
		private constantService: ConstantService,
		private dialog: MatDialog,
		private ruDetailedLocationService: RUDetailedLocationService,
		private formBuilder: UntypedFormBuilder,
		private vrboService: VrboService) {
		this.wifiSecurities = propertyService.wifiSecurities;
		this.locations = constantService.locations;
		this.sitePriorities = propertyService.sitePriorities;
		this.locationIds = this.constantService.locations;
	}

	// structure
	subscription: Subscription;
	routerLocation = this.location;


	// data
	propertyApiResponse: ApiResponse;
	propertyTemplateResApi: ApiResponse;
	property: any;
	properties: any = [];
	shortTermRentalId: number;
	yieldMaxPropertyId: number;
	yieldMaxRateBucketId: number;
	reservationId: number;
	featuresList: Feature[] = [];
	rateBuckets: any = [];
	developments: any = [];


	// forms
	form1: UntypedFormGroup;
	copyFeaturesFromPropertyForm: UntypedFormGroup;
	mergeFeaturesFromPropertyForm: UntypedFormGroup;
	formLoading = false;
	formAction: String;
	formLabels = [];
	formReadOnly = false;
	headlineChannelDefaultFieldLength = 0;
	listingNameLength = 0;
	headlineDefaultLength = 0;
	headlineSpecialOfferLength = 0;
	headlineChannelSpecialOfferLength = 0;
	propDescShortLength = 0;

	isLocationSelected = false;
	isNameLoading = false;

	// Lazy-load toggles (manual) - components render only after user clicks "Load ..."
	featureListsLoaded = false;
	loadState: { [key: string]: boolean } = {
		compositeListingList: false,
		ruPropertiesStatus: false,
		maintenanceEvents: false,
		propertyCondition: false,
		compendiumsJoin: false,
		bedroom: false,
		bathroom: false,
		pictures: false,
		reviews: false,
		rateOverride: false,
		propertyAdmin: false,
		listingQualityStatus: false,
		auditEnriched: false,
		publicTaskList: false,
		yieldMaxProperties: false,
		yieldMaxReservations: false,
		tapeChart: false,
		rateSeason: false,
		qualityTests: false,
		auditList: false,
	};

	loadComponent(key: string): void {
		this.loadState[key] = true;
	}

	loadAllFeatureLists(): void {
		this.featureListsLoaded = true;
	}

	adrTypes = [
		{ value: 'SP', name: 'Subject Property' }
	];

	sizeUnitOfMeasurements = [
		{ value: 'SQUARE_FEET', name: 'Square Feet' }
	];

	keyServiceTypes = [
		{ value: 'KK', name: 'Kaba' },
		{ value: 'KM', name: 'Meet and Greet' },
		{ value: 'KE', name: 'Front Desk' },
		{ value: 'IG', name: 'IglooHome' },
		{ value: 'CB', name: 'Combo Box' },
		{ value: 'GK', name: 'Generic Keypad' },
	];

	propertyRanks = [
		{ value: 1, name: 'Platinum' },
		{ value: 2, name: 'Gold' },
		{ value: 3, name: 'Blue' },
		{ value: 4, name: 'Brown' }
	];

	listingStatuses = [
		{ value: 0, name: 'Active' },
		{ value: 2, name: 'Suspended' },
		{ value: 1, name: 'Archived' }
	];

	listingTypes = [
		{ value: 1, name: 'Short Term Rental' },
		{ value: 2, name: 'Composite' },
		{ value: 3, name: 'Hotel Room Type' }
	];

	propertyTypes = [
		{ value: '14', name: 'Single Family Home' },
		{ value: '01', name: 'Condominium' },
		{ value: '16', name: 'Townhome' }

	];

	propertySubTypes = [
		{ value: '', name: 'Standard' }
	];

	hotelRoomTypes = [
		{ value: 0, name: 'DO NOT SELL HOTEL' },
		{ value: 1, name: 'Studio Ocean View' },
		{ value: 2, name: 'Studio Partial Ocean View' },
		{ value: 3, name: 'Studio Resort View' },
		{ value: 4, name: 'Studio Construction View' },
		{ value: 5, name: '1 Bed Ocean View' },
		{ value: 6, name: '1 Bed Partial Ocean View' },
		{ value: 7, name: '1 Bed Resort View' },
		{ value: 8, name: '1 Bed Construction View' },
		{ value: 9, name: '2 Bed Ocean View' },
		{ value: 10, name: '2 Bed Partial Ocean View' },
		{ value: 11, name: '2 Bed Resort View' },
		{ value: 12, name: '2 Bed Construction View' },
		{ value: 13, name: '3 Bed Ocean View' },
		{ value: 14, name: '3 Bed Partial Ocean View' },
		{ value: 15, name: '3 Bed Resort View' },
		{ value: 16, name: '3 Bed Construction View' },
		{ value: 17, name: '3 Bed Presidential Suite' },
		{ value: 18, name: '4 Bed Ocean View' },
		{ value: 19, name: '4 Bed Partial Ocean View' },
		{ value: 20, name: '4 Bed Resort View' },
		{ value: 21, name: '4 Bed Construction View' },
		{ value: 22, name: '4 Bed Presidential Suite' },
		{ value: 23, name: '5 Bed Ocean View' },
		{ value: 24, name: '5 Bed Partial Ocean View' },
		{ value: 25, name: '5 Bed Resort View' },
		{ value: 26, name: '5 Bed Construction View' },
		{ value: 27, name: '5 Bed Presidential Suite' },
		{ value: 28, name: '6 Bed Ocean View' },
		{ value: 29, name: '6 Bed Partial Ocean View' },
		{ value: 30, name: '6 Bed Resort View' },
		{ value: 31, name: '6 Bed Construction View' },
		{ value: 32, name: '6 Bed Presidential Suite' },
		{ value: 33, name: '6 Bed Mountain View' },
		{ value: 34, name: '6 Bed Partial Mountain View' },
		{ value: 35, name: '6 Bed Treeline View' },
		{ value: 36, name: '6 Bed Partial Treeline View' },
	];

	viewTypes = [
		{ value: 'NV', name: 'No View' },
		{ value: 'GGV', name: 'Garden View' },
		{ value: 'RV', name: 'Resort View' },
		{ value: 'BF', name: 'Beachfront' },
		{ value: 'BV', name: 'Beach View' },
		{ value: 'BP', name: 'Beach View Partial' },
		// { value: 'DT', name: 'Downtown View' },
		// { value: 'POV', name: 'Ocean View Premium' },
		{ value: 'OF', name: 'Oceanfront' },
		{ value: 'OV', name: 'Ocean View' },
		{ value: 'OP', name: 'Ocean View Partial' },
		{ value: 'PK', name: 'Ocean View Peakaboo' },
		{ value: 'LF', name: 'Lakefront' },
		{ value: 'LV', name: 'Lake View' },
		{ value: 'LVP', name: 'Lake View Partial' },
		{ value: 'MV', name: 'Mountain View' },
		{ value: 'PMV', name: 'Partial Mountain' },
		{ value: 'TRE', name: 'Nature View' },
		{ value: 'VAL', name: 'Valley' },
		{ value: 'STR', name: 'Street' },
		{ value: 'HIL', name: 'Hill' },
		{ value: 'CIT', name: 'City' },
		{ value: 'GF', name: 'Golf Course Front' },
		{ value: 'GV', name: 'Golf Course View' },
		{ value: 'GP', name: 'Golf Course View Partial' },
		{ value: 'SK', name: 'Ski In Out' },

	];


	locales = [
		{ value: 'en', name: 'English' }
	];

	booleans = [
		{ value: true, name: 'Yes' },
		{ value: false, name: 'No' }
	];

	countries: any[] =
		[
			{ name: '', value: '' },
			{ name: 'Canada', value: 'CA' },
			{ name: 'United States', value: 'US' },
		];

	orders = [];

	validationMessages = {
		shortTermRentalId: [],
		createDate: [],
		lastUpdate: [],
		locale: [],
		locationId: [],
		listingStatus: [],
		listingType: [],
		propertyCode: [
			{ type: 'required', message: 'Required' },
			{ type: 'maxlength', message: '15 characters maximum' }],
		propertyName: [],
		propertyType: [],
		propertySubType: [],
		slPropertyId: [],
		hotelRoomType: [],
		bedroomNum: [],
		bathroomNum: [],
		sleepsMax: [],
		sleepsMaxInBeds: [],
		numSofaBeds: [],
		rollAwayBedNum: [],
		futonBedNum: [],
		inflatableBedNum: [],
		floorOfProperty: [],
		floorsInBuilding: [],
		yearBuilt: [],
		sizeInterior: [],
		sizeExterior: [],
		sizeLot: [],
		sizeUnitOfMeasurement: [],
		newReservationAlertEmail: [],
		sitePriority: [],

		// financials
		roomTaxRate: [],
		salesTaxRate: [],
		annualRevTargetAmnt: [],
		rateBucketId: [],
		rateBucketAdjusterPct: [],
		guestFeeNightly: [],
		mgmtCommission: [],
		minNightlyRate: [],
		minLengthOfStay: [],
		cleaningFeeDeparture: [],
		cleaningFeeInterim: [],
		showPublicCalendar: [],
		showBookOnline: [],
		flexiblePolicyToggle: [],
		keyServiceType: [],


		// ids
		taxId: [],
		geTaxId: [],
		permitId: [],
		vrboId: [],
		vrboShortTermRentalId: [],
		vrboDisplayId: [],
		bookingComId: [],
		airbnbId: [],
		expediaId: [],
		marriottId: [],
		ruPropertyId: [],
		matterportId: [],
		rULocationId: [],


		// address
		adrType: [],
		adrStreetNumber: [],
		adrStreetName: [],
		adrUnit: [],
		adr2: [],
		adrBuildingName: [],
		adrComplexName: [],
		adrDistrictName: [],
		adrCity: [],
		adrCounty: [],
		adrMetro: [],
		adrCountry: [],
		adrRegion: [],
		adrState: [],
		adrPostalCode: [],
		adrIsland: [],
		adrCrossStreet: [],
		adrLongitude: [],
		adrLatitude: [],
		developmentId: [{ type: 'required', message: 'Required' },
		{ type: 'maxlength', message: '15 characters maximum' }],
		description: [],
		listingName: [],
		propDescShort: [],
		propDesc: [],
		headlineSpecialOffer: [],
		headlineChannelDefault: [],
		headlineChannelSpecialOffer: [],
		headlineKeyWords: [],
		vrboHeadline: [],
		airbnbHeadline: [],
		bookingComHeadline: [],
		expediaHeadline: [],
		googleHeadline: [],
		marriottHeadline: [],
		headlineDefault: [],
		summaryDesc: [],
		benefitsDesc: [],
		specialOfferDesc: [],
		bathroomSummary: [],
		bedroomSummary: [],
		locationDesc: [],
		matteportUri: [],
		propDescShortKeyWords: [],
		sleepsDesc: [],
		petsDesc: [],
		pets: [],
		disabled: [],
		disabledDesc: [],
		consumablesDesc: [],
		termsDesc: [],
		maintenanceIssuesDesc: [],
		featureDesc: [],
		smoking: [],
		safe: [],
		vacationEquipmentDesc: [],
		propertyPhoneNumber: [],
		propertyFaxNumber: [],


		// parking
		parking: [],
		parkingSpacesTotal: [],
		parkingSpacesGaraged: [],
		parkingFee: [],
		parkingFeeDesc: [],


		// views
		viewType: [],


		// instructions
		instructionId: [],
		codeFrontDoor: [],
		codeGarage: [],
		codeGate: [],
		codeHoaGate: [],
		codePool: [],
		codeRecreation: [],
		keyServiceSelfServeDesc: [],
		keyServiceAssistedDesc: [],
		wifiName: [],
		wifiName5g: [],
		wifiPassword: [],
		wifiSecurity: [],
		directionsLinkToProperty: [],
		directionsToProperty: [],
		arrivalInstructions: [],
		contractTerms: [],
		opsNotes: [],
		syncShortName: [],
		propertyRank: [],
		parcelId: []
	};

	getOrders() {
		return [
			{ id: '1', name: 'order 1' },
			{ id: '2', name: 'order 2' },
			{ id: '3', name: 'order 3' },
			{ id: '4', name: 'order 4' }
		];
	}




	async ngOnInit() {
		this.formBuild();
		this.formLoading = true;
		// formAction
		this.formAction = this.router.url.startsWith('/property-add') ? 'add' : this.router.url.startsWith('/property-edit') ? 'edit' : this.router.url.startsWith('/property-view') ? 'view' : '/';

		// left menu jump targets (single-page sections)
		this.buildLeftMenu();

		/**
				 *
				 *
				 *
				 * DATA
				 *
				*/

		// wholesale list of all available features
		this.featuresList = this.activatedRoute.snapshot.data.availablePropertyFeaturesResApi.data.propertyFeatures;
		// used to switch between properties in drop down
		this.fetchProperties();
		this.fetchRateBuckets();
		this.fetchDevelopments();

		// used only if existing property (not on add)
		if (this.formAction != 'add') {
			this.propertyApiResponse = this.activatedRoute.snapshot.data.propertyResApi.data.property;

			this.property = this.propertyApiResponse;
			// Download property images
			// this.property.pictures.forEach(element => {
			// 	window.open(element.url, '');
			// });
			// console.log(JSON.stringify(this.property))
		}




		/**
				 *
				 *
				 *
				 * ACTIONS (ADD / EDIT / DETAIL)
				 *
				 */
		try {
			// add
			if (this.formAction === 'add') {
				this.formLabels['title'] = 'Add New Property';
				this.formReadOnly = false;
			}

			// edit
			else if (this.formAction === 'edit') {

				this.subscription = this.activatedRoute.params.subscribe(params => {
					this.shortTermRentalId = params.shortTermRentalId; // (+) converts string 'id' to a number
					this.reservationId = params.reservationId; // (+) converts string 'id' to a number
				});
				this.formLabels['title'] = 'Edit';
				this.formReadOnly = false;

				if (this.property.rULocationId) {
					try {
						//making this synchronous so that locationId is not populated with null value
						const res = await firstValueFrom(
							this.ruDetailedLocationService.findByLocationId(this.property.rULocationId)
						);

						const locationObj = {
							rUDetailedLocationId: res.data.rUDetailedLocation.rUDetailedLocationId,
							locationId: res.data.rUDetailedLocation.locationId,
							locationTypeId: res.data.rUDetailedLocation.locationTypeId,
							parentLocationId: res.data.rUDetailedLocation.parentLocationId,
							timeZone: res.data.rUDetailedLocation.timeZone,
							name: res.data.rUDetailedLocation.name
						};

						this.locationDetailObj = locationObj;
						this.property.rULocationId = locationObj;

						this.form1.patchValue({
							rULocationId: locationObj
						});
						this.formLoading = false;

					} catch (error) {
						this.alertService.error(error);
					}
				}
				// patch in data
				this.form1.patchValue(this.property);

				this.listingNameLength = this.form1?.get('featureDesc')?.get('listingName')?.value?.length;
				this.headlineChannelDefaultFieldLength = this.form1?.get('featureDesc')?.get('headlineChannelDefault')?.value?.length;
				this.headlineDefaultLength = this.form1?.get('featureDesc')?.get('headlineDefault')?.value?.length;
				this.headlineSpecialOfferLength = this.form1?.get('featureDesc')?.get('headlineSpecialOffer')?.value?.length;
				this.headlineChannelSpecialOfferLength = this.form1?.get('featureDesc')?.get('headlineChannelSpecialOffer')?.value?.length;
				this.propDescShortLength = this.form1?.get('featureDesc')?.get('propDescShort')?.value?.length;

				// console.log(this.property);
			}

			// view
			else if (this.formAction === 'view') {
				this.subscription = this.activatedRoute.params.subscribe(params => {
					this.shortTermRentalId = params.shortTermRentalId; // (+) converts string 'id' to a number
				});
				this.formLabels['title'] = '';
				this.formReadOnly = true;
				this.formBuild();

				// patch in data
				this.form1.patchValue(this.property);
			}
		}
		catch (error) {
			console.log('Error: ' + error);
		}
		this.getYieldMaxPropertyDetails();
		document.body.classList.add('property-page');
	}
	ngOnDestroy(): void {
		document.body.classList.remove('property-page');
		this.destroy$.next();
		this.destroy$.complete();
	}

	ngAfterViewInit(): void {

		/********
		* Count characters
		*
		*/
		this.form1.get('featureDesc').get('listingName').valueChanges.subscribe(value => {
			this.listingNameLength = value ? value.length : 0;
		});

		this.form1.get('featureDesc').get('headlineChannelDefault').valueChanges.subscribe(value => {
			this.headlineChannelDefaultFieldLength = value ? value.length : 0;
		});

		this.form1.get('featureDesc').get('headlineDefault').valueChanges.subscribe(value => {
			this.headlineDefaultLength = value ? value.length : 0;
		});

		this.form1.get('featureDesc').get('headlineSpecialOffer').valueChanges.subscribe(value => {
			this.headlineSpecialOfferLength = value ? value.length : 0;
		});

		this.form1.get('featureDesc').get('headlineChannelSpecialOffer').valueChanges.subscribe(value => {
			this.headlineChannelSpecialOfferLength = value ? value.length : 0;
		});

		this.form1.get('featureDesc').get('propDescShort').valueChanges.subscribe(value => {
			this.propDescShortLength = value ? value.length : 0;
		});

		// If the route has a fragment (e.g. /property-edit/123#section-settings), jump to it
		this.activatedRoute.fragment.subscribe(fragment => {
			if (fragment) {
				// Allow layout to settle
				setTimeout(() => this.scrollToSection(fragment), 0);
			}
		});

		// Scroll-spy highlighting for the left nav
		setTimeout(() => this.initScrollSpy(), 0);

	}

	formBuild() {
		this.copyFeaturesFromPropertyForm = this.formBuilder.group({
			srcShortTermRentalId: [],
			destShortTermRentalId: [this.shortTermRentalId]
		});

		this.mergeFeaturesFromPropertyForm = this.formBuilder.group({
			srcShortTermRentalId: [],
			destShortTermRentalId: [this.shortTermRentalId]
		});

		this.form1 = this.formBuilder.group({
			shortTermRentalId: [this.shortTermRentalId],
			createDate: [],
			lastUpdate: [],
			locale: [],
			locationId: [],
			listingStatus: [],
			listingType: [],
			propertyCode: ['', Validators.required],
			propertyName: [],
			propertyType: [],
			propertySubType: [],
			slPropertyId: [],
			hotelRoomType: [],
			bedroomNum: [],
			bathroomNum: [],
			sleepsMax: [],
			sleepsMaxInBeds: [],
			numSofaBeds: [],
			rollAwayBedNum: [],
			futonBedNum:[],
			inflatableBedNum: [],
			floorOfProperty: [],
			floorsInBuilding: [],
			yearBuilt: [],
			sizeInterior: [],
			sizeExterior: [],
			sizeLot: [],
			sizeUnitOfMeasurement: [],
			newReservationAlertEmail: [],
			sitePriority: [this.property?.sitePriority],

			// financials
			roomTaxRate: [],
			salesTaxRate: [],
			annualRevTargetAmnt: [],
			rateBucketAdjusterPct: [],
			guestFeeNightly: [],
			mgmtCommission: [],
			minNightlyRate: [],
			minLengthOfStay: [],
			cleaningFeeDeparture: [],
			cleaningFeeInterim: [],
			showPublicCalendar: [],
			showBookOnline: [],
			flexiblePolicyToggle: [],
			keyServiceType: [],

			// ids
			taxId: [],
			geTaxId: [],
			permitId: [],
			vrboId: [],
			vrboShortTermRentalId: [],
			vrboDisplayId: [],
			bookingComId: [],
			airbnbId: [],
			expediaId: [],
			marriottId: [],
			ruPropertyId: [],
			matterportId: [],
			rULocationId: [],


			// address
			adrType: [],
			adrStreetNumber: [],
			adrStreetName: [],
			adrUnit: [],
			adr2: [],
			adrBuildingName: [],
			adrComplexName: [],
			adrDistrictName: [],
			adrCity: [],
			adrCounty: [],
			adrMetro: [],
			adrCountry: [],
			adrRegion: [],
			adrState: [],
			adrPostalCode: [],
			adrIsland: [],
			adrCrossStreet: [],
			adrLongitude: [],
			adrLatitude: [],
			syncShortName: [],
			propertyRank: [],
			parcelId: [],


			livingRoom: this.formBuilder.group({
				description: [],
				livingRoomId: [],
			}),

			diningRoom: this.formBuilder.group({
				description: [],
				diningRoomId: [],
			}),

			kitchen: this.formBuilder.group({
				description: [],
				kitchenId: [],
			}),

			balcony: this.formBuilder.group({
				description: [],
				balconyId: [],
			}),

			view: this.formBuilder.group({
				description: [],
				viewType: [],
				viewId: [],
			}),

			entertainment: this.formBuilder.group({
				description: [],
				entertainmentId: [],
			}),

			laundry: this.formBuilder.group({
				description: [],
				laundryId: [],
			}),

			hvac: this.formBuilder.group({
				description: [],
				hvacId: [],
			}),

			linen: this.formBuilder.group({
				description: [],
				linenId: [],
			}),

			exterior: this.formBuilder.group({
				description: [],
				exteriorId: [],
			}),

			parking: this.formBuilder.group({
				parkingId: [],
				parking: [],
				parkingSpacesTotal: [],
				parkingSpacesGaraged: [],
				parkingFee: [],
				parkingFeeDesc: [],
				description: [],
			}),

			children: this.formBuilder.group({
				description: [],
				childrenId: [],
			}),

			instruction: this.formBuilder.group({
				instructionId: [],
				codeFrontDoor: [],
				codeGarage: [],
				codeGate: [],
				codeHoaGate: [],
				codePool: [],
				codeRecreation: [],
				keyServiceSelfServeDesc: [],
				keyServiceAssistedDesc: [],
				wifiName: [],
				wifiName5g: [],
				wifiPassword: [],
				wifiSecurity: ['WPA'],
				directionsLinkToProperty: [],
				directionsToProperty: [],
				arrivalInstructions: [],
				opsNotes: [],
			}),


			featureDesc: this.formBuilder.group({
				featureDescId: [],
				listingName: [],
				propDescShort: [],
				propDesc: [],
				headlineDefault: [],
				headlineSpecialOffer: [],
				headlineChannelDefault: [],
				headlineChannelSpecialOffer: [],
				headlineKeyWords: [],
				vrboHeadline: [],
				airbnbHeadline: [],
				bookingComHeadline: [],
				expediaHeadline: [],
				googleHeadline: [],
				marriottHeadline: [],
				summaryDesc: [],
				benefitsDesc: [],
				specialOfferDesc: [],
				bathroomSummary: [],
				bedroomSummary: [],
				locationDesc: [],
				matteportUri: [],
				propDescShortKeyWords: [],
				sleepsDesc: [],
				petsDesc: [],
				pets: [],
				disabled: [],
				disabledDesc: [],
				consumablesDesc: [],
				termsDesc: [],
				contractTerms: [],
				maintenanceIssuesDesc: [],
				featureDesc: [],
				smoking: [],
				safe: [],
				vacationEquipmentDesc: [],
				propertyPhoneNumber: [],
				propertyFaxNumber: [],
			}),

			rateBucket: this.formBuilder.group({
				rateBucketId: [],
			}),

			development: this.formBuilder.group({
				developmentId: ['', Validators.required],
			}),
		});
	}


	formAdd() {
		try {
			// stop here if form is invalid
			if (this.form1.invalid) {
				return;
			}

			this.formLoading = true;

			this.propertyService.add(this.form1.value)
				.subscribe(
					res => {
						// console.log('property-add SUCCESS: ' + JSON.stringify(res));
						this.shortTermRentalId = res.data.property.shortTermRentalId;
						this.getYieldMaxPropertyDetails();
						this.router.navigate(['/property-view', this.shortTermRentalId]);
					},
					err => {
						console.log('property-add FAIL: ' + JSON.stringify(err));
						this.alertService.error(err);
					},
					() => { this.formLoading = false; });

		} catch (error) {
			this.alertService.error(error);
		}
	}

	// This method for remove Unicode from paragraph
	sanitizeTextPreserveParagraphs(input: string): string {
		return input
			.normalize('NFC') // Normalize Unicode characters
			.replace(/[\u00A0\u200B-\u200F\u2028\u2029\uFEFF]/g, '') // Remove unwanted Unicode characters
			.replace(/[^\S\r\n]+/g, ' ') // Convert irregular spaces (excluding new lines) to single spaces
			.trim(); // Remove leading and trailing spaces
	}


	formUpdate() {
		try {
			// stop here if form is invalid
			if (this.form1.invalid) {
				// return;
			}
			console.log('property-before-updateS: ' + JSON.stringify(this.form1.value));
			this.formLoading = true;
			if (this.form1.value?.rULocationId?.locationId != undefined && this.form1.value?.rULocationId?.locationId != null) {
				this.form1.value.rULocationId = this.form1.value?.rULocationId?.locationId;
			}
			this.form1.value.featureDesc.propDesc = this.removeUnsupportedUtf8Chars(this.form1.value.featureDesc.propDesc);

			this.propertyService.update(this.form1.value)
				.subscribe(
					res => {
						// console.log('property-update SUCCESS: ' + JSON.stringify(res));
						this.shortTermRentalObj = res.data.shortTermRental;
						this.form1.patchValue({
							bedroomNum: this.shortTermRentalObj?.bedroomNum,
							bathroomNum: this.shortTermRentalObj?.bathroomNum,
							sleepsMax: this.shortTermRentalObj?.sleepsMax,
							sleepsMaxInBeds: this.shortTermRentalObj?.sleepsMaxInBeds,
							numSofaBeds: this.shortTermRentalObj?.numSofaBeds,
							sizeInterior: this.shortTermRentalObj?.sizeInterior,
							sizeExterior: this.shortTermRentalObj?.sizeExterior,
							minNightlyRate: this.shortTermRentalObj?.minNightlyRate,
						});
						this.alertService.success(res, true);
						// this.router.navigate(['/property-view', this.shortTermRentalId]);
					},
					err => {
						// Do NOT reset formLoading on error — a 504 means the property update may have
						// already persisted. Keep button disabled to block re-submit.
						console.log('formUpdate (property) FAIL: ' + JSON.stringify(err));
						this.alertService.error(err);
					},
					() => { this.formLoading = false; });
		} catch (error) {
			console.log('Form Invalid: ' + JSON.stringify(error));
			this.alertService.error(error);
			this.formLoading = false;
		}
	}

	formUpdateForCompositeListing() {
		try {
			// stop here if form is invalid
			if (this.form1.invalid) {
				// return;
			}
			console.log('property-before-updateS: ' + JSON.stringify(this.form1.value));
			this.formLoading = true;
			if (this.form1.value?.rULocationId?.locationId != undefined && this.form1.value?.rULocationId?.locationId != null) {
				this.form1.value.rULocationId = this.form1.value?.rULocationId?.locationId;
			}
			this.form1.value.featureDesc.propDesc = this.sanitizeTextPreserveParagraphs(this.form1.value.featureDesc.propDesc);

			this.propertyService.update(this.form1.value)
				.subscribe(
					res => {
						window.location.reload();
						// console.log('property-update SUCCESS: ' + JSON.stringify(res));
						// this.alertService.success(res, true);
						// this.router.navigate(['/property-view', this.shortTermRentalId]);
					},
					err => {
						// Do NOT reset formLoading on error — a 504 means the composite listing update may
						// have already persisted. Keep button disabled to block re-submit.
						console.log('formUpdateForCompositeListing FAIL: ' + JSON.stringify(err));
						this.alertService.error(err);
					},
					() => { this.formLoading = false; });
		} catch (error) {
			console.log('Form Invalid: ' + JSON.stringify(error));
			this.alertService.error(error);
			this.formLoading = false;
		}
	}


	// not used - resolver instead
	findById(id: number) {
		try {
			console.log("dodododo");
			this.formLoading = true;
			this.propertyService.findById(id)
				.subscribe(
					res => {
						// console.log('property-find SUCCESS: ' + JSON.stringify(res));
						this.propertyApiResponse = res;
						this.property = res.data.property;
						this.form1.patchValue(this.property);
					},
					err => {
						console.log('property-find FAIL: ' + JSON.stringify(err));
						this.alertService.error(err);
					},
					() => { this.formLoading = false; });

		} catch (error) {
			this.alertService.error(error);
		}
	}

	// not used - resolver instead
	fetchRateBuckets() {
		try {
			this.formLoading = true;
			this.rateBucketService.findAll()
				.subscribe(
					res => {
						// console.log('rateBucketService SUCCESS: ' + JSON.stringify(res));
						this.rateBuckets = res.data.rateBuckets;
						this.form1.patchValue(this.rateBuckets);
					},
					err => {
						console.log('rateBucketService FAIL: ' + JSON.stringify(err));
						this.alertService.error(err);
					},
					() => { this.formLoading = false; });

		} catch (error) {
			this.alertService.error(error);
		}
	}


	fetchDevelopments() {
		try {
			this.formLoading = true;
			this.reportService.developmentSummaryList()
				.subscribe(
					res => {
						console.log('developmentSummaryList SUCCESS: ' + JSON.stringify(this.developments));
						this.developments = res.data.developmentSummaryList;
						this.form1.patchValue(this.developments);
					},
					err => {
						console.log('developmentSummaryList FAIL: ' + JSON.stringify(err));
						this.alertService.error(err);
					},
					() => { this.formLoading = false; });

		} catch (error) {
			this.alertService.error(error);
		}
	}


	fetchProperties() {
		if (!this.properties || this.properties === undefined || this.properties.length < 1) {
			try {
				this.propertyService.findAllSummary()
					.subscribe(
						res => {
							// console.log('findAllPropertiesSummary: ' + JSON.stringify(res));
							this.properties = res.data.properties;
							return this.properties;
						},
						err => {
							console.log('Feature-Update FAIL: ' + JSON.stringify(err));
							this.alertService.error(err);
						},
						() => { });
			} catch (error) {
				console.log('Form Invalid: ' + JSON.stringify(error));
				this.alertService.error(error);
			}
		}
		else {
			// console.log('properties are ' + this.properties);
			return (this.properties);
		}
	}

	// not used - resolver instead
	findPropertyFeaturesByShortTermRentalId(shortTermRentalId) {
		try {
			this.formLoading = true;
			this.featureService.findAvailablePropertyFeaturesByShortTermRentalId(shortTermRentalId)
				.subscribe(
					res => {
						// console.log('findPropertyFeaturesByShortTermRentalId: ' + JSON.stringify(res));
						this.featuresList = res.data.propertyFeatures;
						// this.alertService.success(res, true);
					},
					err => {
						console.log('Feature-Update FAIL: ' + JSON.stringify(err));
						this.alertService.error(err);
					},
					() => { this.formLoading = false; });
		} catch (error) {
			console.log('Form Invalid: ' + JSON.stringify(error));
			this.alertService.error(error);
		}
	}

	copyFeaturesFromProperty(sourceId: number, destinationId: number) {
		try {
			this.formLoading = true;

			var copyProperty = {
				sourceId: sourceId,
				destinationId: destinationId
			};


			this.propertyService.copyFeaturesFromProperty(copyProperty)
				.subscribe(
					res => {
						// console.log('property-find SUCCESS: ' + JSON.stringify(res));
						this.alertService.success(res, true);

						// now refresh
						this.findPropertyFeaturesByShortTermRentalId(this.shortTermRentalId)
					},
					err => {
						console.log('property-find FAIL: ' + JSON.stringify(err));
						this.alertService.error(err);
					},
					() => { this.formLoading = false; });

		} catch (error) {
			this.alertService.error(error);
		}
	}

	mergeFeaturesFromProperty(sourceId: number, destinationId: number) {
		try {
			this.formLoading = true;

			var mergeProperty = {
				sourceId: sourceId,
				destinationId: destinationId
			};


			this.propertyService.mergeFeaturesFromProperty(mergeProperty)
				.subscribe(
					res => {
						// console.log('property-find SUCCESS: ' + JSON.stringify(res));
						this.alertService.success(res, true);

						// now refresh
						this.findPropertyFeaturesByShortTermRentalId(this.shortTermRentalId)
					},
					err => {
						console.log('property-find FAIL: ' + JSON.stringify(err));
						this.alertService.error(err);
					},
					() => { this.formLoading = false; });

		} catch (error) {
			this.alertService.error(error);
		}
	}

	getDynamicLongPropDescription() {
		try {
			this.formLoading = true;

			this.propertyService.getDynamicLongPropDescription(this.shortTermRentalId)
				.subscribe(
					res => {
						this.formLoading = false;
						// now refresh
						this.dynamicPropDesc = res.data.dynamicPropDesc;

						this.form1.patchValue({
							featureDesc: {
								propDesc: this.dynamicPropDesc
							}
						});
					},
					err => {
						this.formLoading = false;
						console.log('property-find FAIL: ' + JSON.stringify(err));
						this.alertService.error(err);
					},
					() => { });

		} catch (error) {
			this.formLoading = false;
			this.alertService.error(error);
		}
	}

	getDynamicLongPropHeadline() {
		try {
			this.formLoading = true;

			this.propertyService.getDynamicLongPropHeadline(this.shortTermRentalId)
				.subscribe(
					res => {
						this.formLoading = false;
						// now refresh
						this.dynamicHeadlineChannelSpecialOffer = res.data.dynamicHeadlineChannelSpecialOffer;

						this.form1.patchValue({
							featureDesc: {
								headlineChannelSpecialOffer: this.dynamicHeadlineChannelSpecialOffer
							}
						});
					},
					err => {
						this.formLoading = false;
						console.log('property-find FAIL: ' + JSON.stringify(err));
						this.alertService.error(err);
					},
					() => { });

		} catch (error) {
			this.formLoading = false;
			this.alertService.error(error);
		}
	}


	buildLeftMenu(): void {
		this.leftMenuItems = [
			{
				label: 'Settings',
				icon: 'pi pi-cog',
				styleClass: 'nav-parent',
				id: 'section-settings',
				command: () => this.scrollToSection('section-settings')
			},
			{
				label: 'Overview',
				styleClass: 'nav-child',
				id: 'section-settings-overview',
				command: () => this.scrollToSection('section-settings-overview')
			},
			{
				label: 'Financials',
				styleClass: 'nav-child',
				id: 'section-settings-financials',
				command: () => this.scrollToSection('section-settings-financials')
			},
			{
				label: 'Address',
				styleClass: 'nav-child',
				id: 'section-settings-address',
				command: () => this.scrollToSection('section-settings-address')
			},
			{
				label: 'Channels',
				styleClass: 'nav-child',
				id: 'section-settings-channels',
				command: () => this.scrollToSection('section-settings-channels')
			},
			{
				label: 'Operations',
				styleClass: 'nav-child',
				id: 'section-settings-operations',
				command: () => this.scrollToSection('section-settings-operations')
			},
			{
				label: 'Maintenance Event',
				styleClass: 'nav-child',
				id: 'section-settings-maintenance-event',
				command: () => this.scrollToSection('section-settings-maintenance-event')
			},
			{
				label: 'Property Condition',
				styleClass: 'nav-child',
				id: 'section-settings-property-condition',
				command: () => this.scrollToSection('section-settings-property-condition')
			},
			{
				label: 'Compendium',
				styleClass: 'nav-child',
				id: 'section-settings-compendium',
				command: () => this.scrollToSection('section-settings-compendium')
			},
			{
				label: 'Marketing',
				icon: 'pi pi-megaphone',
				styleClass: 'nav-parent',
				id: 'section-marketing',
				command: () => this.scrollToSection('section-marketing')
			},
			{
				label: 'Desc',
				styleClass: 'nav-child',
				id: 'section-marketing-desc',
				command: () => this.scrollToSection('section-marketing-desc')
			},
			{
				label: 'Rooms',
				styleClass: 'nav-child',
				id: 'section-marketing-rooms',
				command: () => this.scrollToSection('section-marketing-rooms')
			},
			{
				label: 'Living',
				styleClass: 'nav-grandchild',
				id: 'marketing-rooms-living',
				command: () => this.scrollToSection('marketing-rooms-living')
			},

			{
				label: 'Dining',
				styleClass: 'nav-grandchild',
				id: 'marketing-rooms-dining',
				command: () => this.scrollToSection('marketing-rooms-dining')
			},

			{
				label: 'Kitchen',
				styleClass: 'nav-grandchild',
				id: 'marketing-rooms-kitchen',
				command: () => this.scrollToSection('marketing-rooms-kitchen')
			},

			{
				label: 'Bedrooms',
				styleClass: 'nav-grandchild',
				id: 'marketing-rooms-bedrooms',
				command: () => this.scrollToSection('marketing-rooms-bedrooms')
			},

			{
				label: 'Bathrooms',
				styleClass: 'nav-grandchild',
				id: 'marketing-rooms-bathrooms',
				command: () => this.scrollToSection('marketing-rooms-bathrooms')
			},

			{
				label: 'Den',
				styleClass: 'nav-grandchild',
				id: 'marketing-rooms-den',
				command: () => this.scrollToSection('marketing-rooms-den')
			},

			{
				label: 'Family',
				styleClass: 'nav-grandchild',
				id: 'marketing-rooms-family',
				command: () => this.scrollToSection('marketing-rooms-family')
			},

			{
				label: 'Fitness',
				styleClass: 'nav-grandchild',
				id: 'marketing-rooms-fitness',
				command: () => this.scrollToSection('marketing-rooms-fitness')
			},

			{
				label: 'Mudroom',
				styleClass: 'nav-grandchild',
				id: 'marketing-rooms-mudroom',
				command: () => this.scrollToSection('marketing-rooms-mudroom')
			},
			{
				label: 'Balcony',
				styleClass: 'nav-grandchild',
				id: 'marketing-rooms-balcony',
				command: () => this.scrollToSection('marketing-rooms-balcony')
			},
			{
				label: 'Features',
				styleClass: 'nav-child',
				id: 'section-marketing-features',
				command: () => this.scrollToSection('section-marketing-features')
			},
			{
				label: 'Access',
				styleClass: 'nav-grandchild',
				id: 'marketing-features-access',
				command: () => this.scrollToSection('marketing-features-access')
			},
			{
				label: 'Exterior',
				styleClass: 'nav-grandchild',
				id: 'marketing-features-exterior',
				command: () => this.scrollToSection('marketing-features-exterior')
			},
			{
				label: 'Views',
				styleClass: 'nav-grandchild',
				id: 'marketing-features-views',
				command: () => this.scrollToSection('marketing-features-views')
			},
			{
				label: 'Electronics',
				styleClass: 'nav-grandchild',
				id: 'marketing-features-electronics',
				command: () => this.scrollToSection('marketing-features-electronics')
			},
			{
				label: 'Laundry',
				styleClass: 'nav-grandchild',
				id: 'marketing-features-laundry',
				command: () => this.scrollToSection('marketing-features-laundry')
			},
			{
				label: 'HVAC',
				styleClass: 'nav-grandchild',
				id: 'marketing-features-hvac',
				command: () => this.scrollToSection('marketing-features-hvac')
			},
			{
				label: 'Linen',
				styleClass: 'nav-grandchild',
				id: 'marketing-features-linen',
				command: () => this.scrollToSection('marketing-features-linen')
			},
			{
				label: 'Parking',
				styleClass: 'nav-grandchild',
				id: 'marketing-features-parking',
				command: () => this.scrollToSection('marketing-features-parking')
			},
			{
				label: 'Children',
				styleClass: 'nav-grandchild',
				id: 'marketing-features-children',
				command: () => this.scrollToSection('marketing-features-children')
			},

			{
				label: 'Floorplan',
				styleClass: 'nav-child',
				id: 'section-marketing-floorplan',
				command: () => this.scrollToSection('section-marketing-floorplan')
			},
			{
				label: 'Amenities',
				styleClass: 'nav-child',
				id: 'section-marketing-amenities',
				command: () => this.scrollToSection('section-marketing-amenities')
			},
			{
				label: 'Overrides',
				styleClass: 'nav-child',
				id: 'section-marketing-overrides',
				command: () => this.scrollToSection('section-marketing-overrides')
			},
			{
				label: 'Media',
				styleClass: 'nav-child',
				id: 'section-marketing-media',
				command: () => this.scrollToSection('section-marketing-media')
			},
			{
				label: 'Rates & Yield',
				icon: 'pi pi-chart-line',
				styleClass: 'nav-parent',
				id: 'section-rates-yield',
				command: () => this.scrollToSection('section-rates-yield')
			},
			{
				label: 'YieldMax Property',
				styleClass: 'nav-child',
				id: 'section-rates-yield-property',
				command: () => this.scrollToSection('section-rates-yield-property')
			},
			{
				label: 'Reservation',
				styleClass: 'nav-child',
				id: 'section-rates-yield-reservation',
				command: () => this.scrollToSection('section-rates-yield-reservation')
			},
			{
				label: 'Setting',
				styleClass: 'nav-child',
				id: 'section-rates-yield-setting',
				command: () => this.scrollToSection('section-rates-yield-setting')
			},
			{
				label: 'Rate',
				styleClass: 'nav-child',
				id: 'section-rates-yield-rate',
				command: () => this.scrollToSection('section-rates-yield-rate')
			},
			{
				label: 'Seasons',
				styleClass: 'nav-child',
				id: 'section-rates-yield-seasons',
				command: () => this.scrollToSection('section-rates-yield-seasons')
			},
			{
				label: 'Quality Test',
				styleClass: 'nav-child',
				id: 'section-rates-yield-quality-test',
				command: () => this.scrollToSection('section-rates-yield-quality-test')
			},
			{
				label: 'Admin',
				styleClass: 'nav-child',
				id: 'section-admin',
				command: () => this.scrollToSection('section-admin')
			},
			{
				label: 'Quality',
				styleClass: 'nav-child',
				id: 'section-quality',
				command: () => this.scrollToSection('section-quality')
			},
			{
				label: 'History-Enriched',
				styleClass: 'nav-child',
				id: 'section-history-enriched',
				command: () => this.scrollToSection('section-history-enriched')
			},
			{
				label: 'Tasks',
				styleClass: 'nav-child',
				id: 'section-tasks',
				command: () => this.scrollToSection('section-tasks')
			}
		];
		this.buildNavSearchOptions();

	}

	/**
	 * Select a Rates & Yield sub-tab and then scroll to the Rates & Yield section.
	 * This keeps the left navigation consistent with the single-page layout.
	 */
	scrollToRatesYieldTab(tabIndex: number): void {
		this.ratesYieldSelectedIndex = tabIndex;
		// Give Angular a tick to apply selectedIndex before scrolling.
		setTimeout(() => this.scrollToSection('section-rates-yield'), 0);
	}

	navigateToSection(sectionId: string): void {
		if (!sectionId) return;

		// Ensure any lazily-rendered components in this section exist before we scroll.
		this.ensureSectionLoaded(sectionId);

		// Mark as programmatic scroll so scroll-spy doesn't "bounce" while smooth scrolling.
		this.isProgrammaticScrollUntil = Date.now() + 900;
		this.activeSectionId = sectionId;

		// Scroll the active nav item into view in the aside panel
		this.scrollActiveNavIntoView();

		// Update URL fragment (so deep-links work) while preserving query params.
		this.router.navigate([], {
			relativeTo: this.activatedRoute,
			fragment: sectionId,
			replaceUrl: false,
			queryParamsHandling: 'preserve'
		}).then(() => {
			this.scrollToSection(sectionId);
		});
	}


	private buildNavSearchOptions(): void {
		const opts: { label: string; id: string }[] = [];

		const walk = (items: MenuItem[] | undefined) => {
			if (!items) return;
			for (const item of items) {
				const label = (item?.label ?? '').toString().trim();
				const id = (item as any)?.id as string | undefined; // MenuItem supports `id`, but not strongly typed in older versions
				if (label && id) {
					opts.push({ label, id });
				}
				if (item?.items?.length) {
					walk(item.items);
				}
			}
		};

		walk(this.leftMenuItems);
		// De-dupe by id (keep first)
		const seen = new Set<string>();
		this.navSearchOptions = opts.filter(o => {
			if (seen.has(o.id)) return false;
			seen.add(o.id);
			return true;
		});
	}

	filterNavSearch(event: any): void {
		const q = (event?.query ?? '').toString().toLowerCase();
		this.navSearchSuggestions = this.navSearchOptions
			.filter(o => o.label.toLowerCase().includes(q))
			.slice(0, 25);
	}

	onNavSearchSelect(event: any): void {
		const selected = event?.value ?? event;
		if (selected?.id) {
			this.navigateToSection(selected.id);
		}
		this.navSearchCtrl.setValue(null, { emitEvent: false });
	}
	// ---- Lazy-load via left nav / section navigation ----
	// Map section IDs to the lazy-load flags they require.
	private sectionLoadMap: Record<string, Array<string | 'FEATURE_LISTS'>> = {
		// Settings
		'section-settings-maintenance-event': ['maintenanceEvents'],
		'section-settings-property-condition': ['propertyCondition'],
		'section-settings-compendium': ['compendiumsJoin'],
		'section-settings-channels': ['ruPropertiesStatus'],

		// Marketing rooms
		'marketing-rooms-bedrooms': ['bedroom'],
		'marketing-rooms-bathrooms': ['bathroom'],

		// Any section that contains lots of <app-feature-list>
		'section-marketing-features': ['FEATURE_LISTS'],
		'marketing-features-access': ['FEATURE_LISTS'],
		'marketing-features-exterior': ['FEATURE_LISTS'],
		'marketing-features-views': ['FEATURE_LISTS'],
		'marketing-features-electronics': ['FEATURE_LISTS'],
		'marketing-features-laundry': ['FEATURE_LISTS'],
		'marketing-features-hvac': ['FEATURE_LISTS'],
		'marketing-features-linen': ['FEATURE_LISTS'],
		'marketing-features-parking': ['FEATURE_LISTS'],
		'section-marketing-floorplan': ['FEATURE_LISTS'],
		'section-marketing-amenities': ['FEATURE_LISTS'],
	};

	private ensureSectionLoaded(sectionId: string): void {
		const keys = this.sectionLoadMap?.[sectionId];
		if (!keys?.length) return;

		for (const key of keys) {
			if (key === 'FEATURE_LISTS') {
				this.featureListsLoaded = true;
			} else {
				(this.loadState as any)[key] = true;
			}
		}
	}


	scrollToSection(sectionId: string): void {
		// Ensure any lazy-loaded section content is rendered before we try to scroll.
		this.ensureSectionLoaded(sectionId);

		const el = document.getElementById(sectionId);
		if (!el) {
			// Section may not exist yet if it was gated behind *ngIf; wait a tick and retry.
			setTimeout(() => {
				const retryEl = document.getElementById(sectionId);
				retryEl?.scrollIntoView({ behavior: 'smooth', block: 'start' });
			});
			return;
		}

		// In some layouts the content panel is the scroll container, while in others
		// the browser window scrolls. Try the panel only if it is actually scrollable;
		// otherwise scroll the window.
		const container = document.querySelector('.property-edit-content') as HTMLElement | null;
		const containerIsScrollable = !!container && (container.scrollHeight - container.clientHeight) > 5;

		if (container && containerIsScrollable) {
			const containerRect = container.getBoundingClientRect();
			const elRect = el.getBoundingClientRect();
			const currentScroll = container.scrollTop;
			const targetTop = (elRect.top - containerRect.top) + currentScroll - this.sectionHeaderOffsetPx;
			container.scrollTo({ top: Math.max(0, targetTop), left: 0, behavior: 'smooth' });
			return;
		}

		// Window scroll (most common)
		const y = el.getBoundingClientRect().top + window.scrollY - this.sectionHeaderOffsetPx;
		window.scrollTo({ top: Math.max(0, y), left: 0, behavior: 'smooth' });
	}

	/**
	 * Initialize IntersectionObserver to highlight the active left-nav item while scrolling.
	 * Falls back gracefully (no highlight) in older browsers / test environments.
	 */
	/**
	 * Scroll spy (no IntersectionObserver):
	 * - Computes a stable, ordered list of section offsets
	 * - On scroll, sets activeSectionId to the last section whose top is above the viewport + offset
	 * This avoids the "bouncing" you saw when many nested sections overlap visibility thresholds.
	 */
	private initScrollSpy(): void {
		if (typeof window === 'undefined') return;

		// Build initial order + offsets after DOM settles
		setTimeout(() => {
			this.refreshSectionOrder();
			this.updateActiveSectionFromScroll();
		}, 0);

		fromEvent(window, 'scroll')
			.pipe(auditTime(50), takeUntil(this.destroy$))
			.subscribe(() => this.updateActiveSectionFromScroll());

		fromEvent(window, 'resize')
			.pipe(auditTime(200), takeUntil(this.destroy$))
			.subscribe(() => {
				this.refreshSectionOrder();
				this.updateActiveSectionFromScroll();
			});
	}

	private refreshSectionOrder(): void {
		const ids = (this.leftMenuItems || [])
			.map(i => (i as any)?.id as string | undefined)
			.filter((id): id is string => !!id);

		const uniqueIds = Array.from(new Set(ids));
		const pairs: Array<{ id: string; top: number }> = [];

		for (const id of uniqueIds) {
			const el = document.getElementById(id);
			if (!el) continue;
			const top = (el as HTMLElement).getBoundingClientRect().top + window.scrollY;
			pairs.push({ id, top });
		}

		pairs.sort((a, b) => a.top - b.top);

		this.sectionOrder = pairs.map(p => p.id);
		this.sectionTops = pairs.reduce((acc, p) => {
			acc[p.id] = p.top;
			return acc;
		}, {} as Record<string, number>);
	}

	private updateActiveSectionFromScroll(): void {
		if (typeof window === 'undefined') return;
		if (!this.sectionOrder.length) return;

		// Don't fight the UI while we're doing a programmatic smooth-scroll
		if (Date.now() < this.isProgrammaticScrollUntil) return;

		const y = window.scrollY + this.scrollSpyOffsetPx;
		let active: string | null = null;

		for (const id of this.sectionOrder) {
			const top = this.sectionTops[id];
			if (top <= y) active = id;
			else break;
		}

		if (active && active !== this.activeSectionId) {
			this.activeSectionId = active;
			// Scroll the active nav item into view in the aside panel
			this.scrollActiveNavIntoView();
		}
	}

	/**
	 * Scrolls the currently active navigation item into view within the aside panel.
	 * This ensures users can always see which section is highlighted.
	 */

	private scrollActiveNavIntoView(): void {
		if (!this.activeSectionId) return;

		requestAnimationFrame(() => {
			const sidebar = document.querySelector<HTMLElement>('.property-edit-sidebar');
			const activeLink = sidebar?.querySelector<HTMLElement>(
				`a[href="#${this.activeSectionId}"]`
			);

			if (!sidebar || !activeLink) return;

			// If sidebar does not scroll, do nothing
			if (sidebar.scrollHeight <= sidebar.clientHeight) return;

			const listItem = activeLink.closest<HTMLElement>('.property-left-nav-item');
			if (!listItem) return;

			const itemTop = listItem.offsetTop;
			const itemBottom = itemTop + listItem.offsetHeight;
			const viewTop = sidebar.scrollTop;
			const viewBottom = viewTop + sidebar.clientHeight;

			// Already visible → no scroll
			if (itemTop >= viewTop && itemBottom <= viewBottom) return;

			// Scroll just enough to bring it into view
			sidebar.scrollTo({
				top:
					itemTop < viewTop
						? itemTop - 16
						: itemBottom - sidebar.clientHeight + 16,
				behavior: 'smooth'
			});
		});
	}



	/**
	 * Simple click handler for our custom left-nav links.
	 * We purposely avoid relying on PrimeNG's menubar click handling here
	 * (which can be affected by overlays / routerLink bindings in complex pages).
	 */
	onLeftNavClick(event: MouseEvent, item: MenuItem): void {
		// Keep the sidebar links behaving like in-page anchors
		event.preventDefault();
		event.stopPropagation();

		// Prefer the MenuItem command if present (existing buildLeftMenu sets it)
		if (typeof item.command === 'function') {
			item.command({ originalEvent: event, item });
			return;
		}

		// Fallback: if someone later sets item.id to a section id
		const maybeId = (item as any)?.id as string | undefined;
		if (maybeId) {
			this.navigateToSection(maybeId);
		}
	}


	changeProperty(event) {
		console.log('changeProperty ' + event.value);
		// used to trigger ngChanges on sub-components
		this.shortTermRentalId = event.value;
		this.findById(event.value);
		this.findPropertyFeaturesByShortTermRentalId(event.value);
	}

	onCopyFeaturesFromProperty() {
		console.log('copyFeaturesFromPropertyForm ' + this.copyFeaturesFromPropertyForm.get('srcShortTermRentalId').value);
		this.copyFeaturesFromProperty(this.copyFeaturesFromPropertyForm.get('srcShortTermRentalId').value, this.shortTermRentalId);
	}

	onMergeFeaturesFromProperty() {
		console.log('onMergeFeaturesFromProperty ' + this.mergeFeaturesFromPropertyForm.get('srcShortTermRentalId').value);
		this.mergeFeaturesFromProperty(this.mergeFeaturesFromPropertyForm.get('srcShortTermRentalId').value, this.shortTermRentalId);
	}

	// recursive
	getFormErrors(form: AbstractControl) {
		if (form instanceof UntypedFormControl) {
			// Return FormControl errors or null
			return form.errors ?? null;
		}
		if (form instanceof UntypedFormGroup) {
			const groupErrors = form.errors;
			// Form group can contain errors itself, in that case add'em
			const formErrors = groupErrors ? { groupErrors } : {};
			Object.keys(form.controls).forEach(key => {
				// Recursive call of the FormGroup fields
				const error = this.getFormErrors(form.get(key));
				if (error !== null) {
					// Only add error if not null
					formErrors[key] = error;
				}
			});
			// Return FormGroup errors or null
			return Object.keys(formErrors).length > 0 ? formErrors : null;
		}
	}

	// search name of location
	changeNameFilter(name: string) {
		this.isNameLoading = true;
		this.isLocationSelected = false;
		this.propertyService.search(name).then(value => {
			console.log(value);
			this.isNameLoading = false;
			this.filteredNames = value.search.map((item: any) => {
				return {
					rUDetailedLocationId: item.rUDetailedLocationId,
					locationId: item.locationId,
					locationTypeId: item.locationTypeId,
					parentLocationId: item.parentLocationId,
					timeZone: item.timeZone,
					name: item.name
				}
			});
			console.log(this.filteredNames)
		})
	}

	onTabChange(event: MatTabChangeEvent): void {
		if (event.tab.textLabel == 'Rates & Yield') {
			this.isRateAndYMTabSelected = true; // hide wiki update button
			if (!this.yieldMaxPropertyId) {
				this.getYieldMaxPropertyDetails();
			}
		}
		else {
			this.isRateAndYMTabSelected = false;
		}
	}

	getYieldMaxPropertyDetails() {
		console.log('getYieldMaxPropertyDetails ' + this.shortTermRentalId);
		if (this.shortTermRentalId != null) {
			this.propertyService.findYieldMaxPropertyByExtSystemId(this.shortTermRentalId)
				.subscribe(
					res => {
						if (res?.data?.property) {
							this.yieldMaxPropertyId = res.data.property.propertyId;
							this.yieldMaxRateBucketId = res.data.property.rateBucketId;
						}
					},
					err => {
						console.log('property-find FAIL: ' + JSON.stringify(err));
					},
					() => { this.formLoading = false; });
		}
	}
	/*********
		* Edit Button
		*
		*/
	onOpenQRCodeComponent() {

		const dialogConfig = new MatDialogConfig();

		// settings
		dialogConfig.disableClose = false;
		dialogConfig.autoFocus = true;
		dialogConfig.hasBackdrop = true;

		// pass data, ie rateBuicket: this.shortTermRentalId,
		dialogConfig.data = {
			property: this.property,
		};

		// open the popup and get a reference to it called dialogRef
		const dialogRef = this.dialog.open(WifiQrComponent, dialogConfig);


	}

	/**
* Removes characters that require 4 bytes in UTF-8 (i.e., code points > U+FFFF).
* MySQL utf8 only supports up to 3 bytes (i.e., U+0000 to U+FFFF).
*/
	removeUnsupportedUtf8Chars(input: string): string {
		if (input == null) return input;
		let result = '';
		let i = 0;

		while (i < input.length) {
			const codePoint = input.codePointAt(i);
			if (codePoint !== undefined && codePoint <= 0xFFFF) {
				result += input[i];
				i++;
			} else {
				// Skip surrogate pair (4-byte character)
				i += 2;
			}
		}

		return result;
	}

	flusPropertyFromVRBO() {
		console.log('Flush  VRBO property: ' + JSON.stringify(event));
		this.confirmationService.confirm({
			message: 'Are you sure you want to Flush this VRBO property?',
			header: 'Confirm Flush',
			icon: 'pi pi-exclamation-triangle',
			accept: () => {
				this.formUpdateForFlusVRBOProperties()
			},
			reject: () => {
				this.messageService.add({ severity: 'info', summary: 'Canceled', detail: 'VRBO property flush canceled.', life: 3000 });
			}
		});
	}

	formUpdateForFlusVRBOProperties() {
		try {
			// Validate that shortTermRentalId is available
			if (!this.shortTermRentalId) {
				this.messageService.add({
					severity: 'error',
					summary: 'Error',
					detail: 'Property ID is required for flush',
					life: 5000
				});
				return;
			}

			this.formLoading = true;

			// Call the migration API endpoint
			this.vrboService.migrateProperty(this.shortTermRentalId)
				.subscribe(
					res => {
						console.log('VRBO property flush SUCCESS: ' + JSON.stringify(res));

						// Show success message with checklist from API response
						this.messageService.add({
							severity: 'success',
							summary: 'Flush Successful',
							detail: res.message || 'Property flushed successfully',
							life: 10000
						});

						// Refresh the property data to get updated values
						this.refreshPropertyAfterMigration();
					},
					err => {
						console.log('VRBO property flush FAIL: ' + JSON.stringify(err));
						this.alertService.error(err);
						this.formLoading = false;
					},
					() => { this.formLoading = false; });
		} catch (error) {
			console.log('VRBO property flush error: ' + JSON.stringify(error));
			this.alertService.error(error);
			this.formLoading = false;
		}
	}

	refreshPropertyCache() {
		this.confirmationService.confirm({
			message: 'Are you sure you want to refresh property cache for this property across servers?',
			header: 'Confirm Cache Refresh',
			icon: 'pi pi-exclamation-triangle',
			accept: () => this.refreshPropertyCacheById(),
			reject: () => {
				this.messageService.add({ severity: 'info', summary: 'Canceled', detail: 'Property cache refresh canceled.', life: 3000 });
			}
		});
	}

	private refreshPropertyCacheById() {
		try {
			if (!this.shortTermRentalId) {
				this.messageService.add({
					severity: 'error',
					summary: 'Error',
					detail: 'Property ID is required for cache refresh',
					life: 5000
				});
				return;
			}

			this.formLoading = true;
			this.propertyService.refreshPropertyCache(this.shortTermRentalId)
				.subscribe(
					res => {
						this.messageService.add({
							severity: 'success',
							summary: 'Success',
							detail: res?.message || 'Property cache refresh completed.',
							life: 8000
						});
					},
					err => {
						console.log('Property cache refresh FAIL: ' + JSON.stringify(err));
						this.alertService.error(err);
						this.formLoading = false;
					},
					() => { this.formLoading = false; });
		} catch (error) {
			console.log('Property cache refresh error: ' + JSON.stringify(error));
			this.alertService.error(error);
			this.formLoading = false;
		}
	}

	refreshPropertyAfterMigration() {
		// Reload the property to get updated values after migration
		if (this.shortTermRentalId) {
			this.formLoading = true;
			this.propertyService.findById(this.shortTermRentalId)
				.subscribe(
					res => {
						console.log('Property refreshed after flush: ' + JSON.stringify(res));
						this.propertyApiResponse = res;
						this.property = res.data.property;

						// Update shortTermRentalObj if available in response
						if (res.data.shortTermRental) {
							this.shortTermRentalObj = res.data.shortTermRental;
						}

						// Patch the entire form with refreshed property data
						this.form1.patchValue(this.property);
					},
					err => {
						console.log('Error refreshing property after flush: ' + JSON.stringify(err));
						this.alertService.error(err);
						this.formLoading = false;
					},
					() => { this.formLoading = false; });
		}
	}

	// AI Integration
	showAiDialog = false;
	aiTargetField: string | null = null;
	aiTargetAgentId: number | null = null;

	openAiDialog(field: string, aiAgentId?: number | null) {
	  this.aiTargetField = field;
	  this.aiTargetAgentId = aiAgentId ?? null;
	  this.showAiDialog = true;
	}

	onCopyResponse(response: string) {
	  // If you're targeting nested FeatureDesc fields (propDesc, propDescShort, etc.)
	  if (this.aiTargetField) {
	    const featureDescGroup = this.form1.get('featureDesc') as UntypedFormGroup | null;

	    if (featureDescGroup?.get(this.aiTargetField)) {
	      featureDescGroup.get(this.aiTargetField)?.setValue(response);
	      featureDescGroup.get(this.aiTargetField)?.markAsDirty();
	      featureDescGroup.get(this.aiTargetField)?.updateValueAndValidity();
	    } else {
	      // fallback for top-level fields, if you ever use them
	      const ctrl = this.form1.get(this.aiTargetField);
	      ctrl?.setValue(response);
	      ctrl?.markAsDirty();
	      ctrl?.updateValueAndValidity();
	    }

	    console.log(`AI response copied to form field ${this.aiTargetField}`);
	  }

	  this.showAiDialog = false;
	  this.aiTargetField = null;
	}


	onDialogHide() {
		this.aiTargetField = null;
	}


}
